import React from "react";
import {
  GridAggregationModel,
  GridColumnMenu,
  GridPaginationModel,
  GridRowSelectionModel,
  isAutogeneratedRow,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import StripedDataGrid from "../common/StripedDataGrid";
import VendorDocumentsToolbar from "./VendorDocumentsToolbar";
import { getVendorDocuments } from "../../api/vendorDocuments";
import { createVendorDocumentColumns } from "./vendorDocumentColumns";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import {
  applySavedColumnOrder,
  buildGridStateStorageKey,
  readGridControlledModels,
} from "../../utils/gridStatePersistence";

type Props = {
  companyId: string;
  vendorId: string;
  onRowClick?: (id: string) => void;
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function VendorDocumentsColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

export default function VendorDocumentsGrid({
  companyId,
  vendorId,
  onRowClick,
}: Props) {
  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const gridStorageKey = React.useMemo(
    () =>
      buildGridStateStorageKey({
        table: "vendor-documents",
        companyId,
      }),
    [companyId]
  );

  const { initialState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey
  );

  const allowedPageSizes = React.useMemo(() => [10, 25, 50, 100], []);

  const normalizePaginationModel = React.useCallback(
    (model: GridPaginationModel): GridPaginationModel => {
      const pageSize = allowedPageSizes.includes(model.pageSize)
        ? model.pageSize
        : 10;

      return {
        page: Math.max(0, model.page ?? 0),
        pageSize,
      };
    },
    [allowedPageSizes]
  );

  const defaultControlledModels = React.useMemo(
    () => ({
      paginationModel: {
        page: 0,
        pageSize: 10,
      },
      sortModel: [],
      filterModel: {
        items: [],
      },
    }),
    []
  );

  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>(() =>
      normalizePaginationModel(
        readGridControlledModels(
          gridStorageKey,
          defaultControlledModels
        ).paginationModel
      )
    );

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const [rows, setRows] = React.useState<any[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    const restored = readGridControlledModels(
      gridStorageKey,
      defaultControlledModels
    );

    setPaginationModel(
      normalizePaginationModel(restored.paginationModel)
    );
  }, [gridStorageKey, defaultControlledModels, normalizePaginationModel]);

  React.useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));

    setSelectedIds(new Set());
  }, [vendorId]);

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const baseColumns = React.useMemo(
    () => createVendorDocumentColumns(t),
    [t]
  );

  const columns = React.useMemo(
    () => applySavedColumnOrder(baseColumns, initialState),
    [baseColumns, initialState]
  );

  const selectedRows = React.useMemo(() => {
    return rows.filter((r) => selectedIds.has(r.id));
  }, [rows, selectedIds]);

  const isRealDocumentRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (!params.row.id) return false;

    return true;
  }, []);

  const handleSelectionChange = React.useCallback(
    (newModel: GridRowSelectionModel) => {
      setSelectedIds((prev) => {
        const updated = new Set(prev);
        const pageIds = new Set(rows.map((r) => r.id));

        if (newModel.type === "include") {
          const newIds = newModel.ids as Set<string>;

          pageIds.forEach((id) => updated.delete(id));
          newIds.forEach((id) => updated.add(id));
        }

        if (newModel.type === "exclude") {
          const excludedIds = newModel.ids as Set<string>;

          pageIds.forEach((id) => {
            if (excludedIds.has(id)) {
              updated.delete(id);
            } else {
              updated.add(id);
            }
          });
        }

        return updated;
      });
    },
    [rows]
  );

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(normalizePaginationModel(model));
      scheduleSave();
    },
    [normalizePaginationModel, scheduleSave]
  );

  const handleScheduleSave = React.useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  const handleRowClick = React.useCallback(
    (params: any) => {
      if (!isRealDocumentRow(params)) {
        return;
      }

      onRowClick?.(String(params.row.id));
    },
    [isRealDocumentRow, onRowClick]
  );

  React.useEffect(() => {
    let cancelled = false;

    setLoading(true);

    getVendorDocuments(
      companyId,
      vendorId,
      paginationModel.page,
      paginationModel.pageSize
    )
      .then((res) => {
        if (cancelled) return;

        setRows(res.items);
        setRowCount(res.total);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error("Failed to load vendor documents", err);
        setRows([]);
        setRowCount(0);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, vendorId, paginationModel]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={initialState}
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        isRowSelectable={isRealDocumentRow}
        rowSelectionModel={{
          type: "include",
          ids: new Set(
            rows
              .filter((r) => selectedIds.has(r.id))
              .map((r) => r.id)
          ),
        }}
        onRowSelectionModelChange={handleSelectionChange}
        disableRowSelectionOnClick
        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onSortModelChange={handleScheduleSave}
        onFilterModelChange={handleScheduleSave}
        onColumnOrderChange={handleScheduleSave}
        onColumnWidthChange={handleScheduleSave}
        onColumnVisibilityModelChange={handleScheduleSave}
        onDensityChange={handleScheduleSave}
        onPreferencePanelClose={handleScheduleSave}
        onRowClick={handleRowClick}
        showToolbar
        slots={{
          toolbar: VendorDocumentsToolbar as any,
          columnMenu: VendorDocumentsColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            total: rowCount,
            columns,
            rows,
            selectedRows,
            selectedCount: selectedIds.size,
          } as any,
        }}
      />
    </div>
  );
}